import React, { useEffect, useState } from "react";

import Modal from "react-bootstrap/Modal";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import ProgressBar from "react-bootstrap/ProgressBar";

import { apiGet } from "../../api/server"

/*
 * Videos, images and music for speed networking, kept in S3 under
 * fabe/speed-networking/<kind>/ via BB-Server's intranetmedia routes. Files are
 * public: anyone with the link can open them, which the video player needs.
 *
 * With onSelect, "Use" hands back the file's link (and a new upload is used
 * straight away). Without it, each file has "Copy link".
 *
 * The limits and file types mirror BB-Server (routes/intranet/intranet-media.js),
 * so a wrong file is caught before it's sent.
 */

const KINDS = {
  videos: { title: "Videos", label: "Video", maxMB: 50, types: ["mp4", "webm"] },
  images: { title: "Images", label: "Image", maxMB: 10, types: ["png", "jpg", "jpeg", "webp", "gif"] },
  music: { title: "Music", label: "Music", maxMB: 20, types: ["mp3", "m4a", "wav", "ogg"] }
}

function formatSize(bytes){
  return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function formatDate(date){
  return new Date(date).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" })
}

// fetch can't report upload progress, so this uses XMLHttpRequest.
function uploadFile(kind, file, onProgress){
  return new Promise((resolve, reject) => {
    const fd = new FormData()
    fd.append("kind", kind)
    fd.append("file", file)
    const xhr = new XMLHttpRequest()
    xhr.open("POST", `${process.env.REACT_APP_SERVER_ADDRESS}/intranetmedia/upload?key=${process.env.REACT_APP_SERVER_API_KEY}`)
    xhr.upload.onprogress = (e) => { if(e.lengthComputable) onProgress(Math.round(e.loaded / e.total * 100)) }
    xhr.onload = () => {
      try { resolve(JSON.parse(xhr.responseText)) }
      catch (err) { reject(err) }
    }
    xhr.onerror = () => reject(new Error("Upload failed"))
    xhr.send(fd)
  })
}

const Preview = ({ kind, url }) => {
  // Navy behind images, so white logos on transparent backgrounds show up.
  if(kind === "images") return <img src={url} alt="" style={{maxWidth: "80px", maxHeight: "45px", background: "#28306d", padding: "2px"}} />
  if(kind === "music") return <audio src={url} controls preload="none" style={{height: "30px", maxWidth: "220px"}} />
  return null
}

const MediaFolder = ({ kind, onSelect, selectedUrl }) => {
  const settings = KINDS[kind]
  const [files, setFiles] = useState(null)
  const [message, setMessage] = useState(null) // { variant, text }
  const [progress, setProgress] = useState(null)
  const [copied, setCopied] = useState(null)

  useEffect(() => {
    apiGet(`intranetmedia/list?kind=${kind}`)
      .then(res => res.json())
      .then(data => {
        if(data.status === "ok") setFiles(data.files)
        else { setFiles([]); setMessage({ variant: "danger", text: data.error || "Couldn't load the files" }) }
      })
      .catch(err => { console.log(err); setFiles([]); setMessage({ variant: "danger", text: "Couldn't load the files" }) })
  }, [kind])

  function upload(e){
    const file = e.target.files[0]
    e.target.value = ""
    if(!file) return
    const ext = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : ""
    if(!settings.types.includes(ext)){
      return setMessage({ variant: "danger", text: `${settings.label} files must be one of: ${settings.types.join(", ")}` })
    }
    if(file.size > settings.maxMB * 1024 * 1024){
      return setMessage({ variant: "danger", text: `That file is too big. ${settings.label} files can be up to ${settings.maxMB} MB.` })
    }
    setMessage(null)
    setProgress(0)
    uploadFile(kind, file, setProgress)
      .then(data => {
        setProgress(null)
        if(data.status !== "ok") return setMessage({ variant: "danger", text: data.error || "Couldn't upload the file" })
        setFiles(prev => [data.file, ...(prev || [])])
        setMessage({ variant: "success", text: `Uploaded ${data.file.name}.` })
        if(onSelect) onSelect(data.file.url)
      })
      .catch(err => { console.log(err); setProgress(null); setMessage({ variant: "danger", text: "Couldn't upload the file" }) })
  }

  function copyLink(url){
    navigator.clipboard.writeText(url).then(() => {
      setCopied(url)
      setTimeout(() => setCopied(null), 2000)
    })
  }

  return (
    <div style={{paddingTop: "15px"}}>
      <Form.Group controlId={`mediaLibrary.upload.${kind}`}>
        <Form.Label>Upload a new file ({settings.types.join(", ")}, up to {settings.maxMB} MB)</Form.Label>
        <Form.File accept={settings.types.map(type => `.${type}`).join(",")} onChange={upload} disabled={progress !== null} />
      </Form.Group>
      {progress !== null && <ProgressBar now={progress} label={`${progress}%`} style={{marginBottom: "15px"}} />}
      {message && <Alert variant={message.variant} transition={false}>{message.text}</Alert>}

      {files === null ? <p>Loading…</p> : files.length === 0 ? <p>No {settings.label.toLowerCase()} files yet.</p> : (
        <Table size="sm" hover responsive>
          <thead>
            <tr><th>Name</th>{kind !== "videos" && <th></th>}<th>Size</th><th>Uploaded</th><th></th></tr>
          </thead>
          <tbody>
            {files.map(file => (
              <tr key={file.url} className={file.url === selectedUrl ? "table-primary" : undefined}>
                <td style={{verticalAlign: "middle", wordBreak: "break-all"}}><a href={file.url} target="_blank" rel="noopener noreferrer">{file.name}</a></td>
                {kind !== "videos" && <td style={{verticalAlign: "middle"}}><Preview kind={kind} url={file.url} /></td>}
                <td style={{verticalAlign: "middle", whiteSpace: "nowrap"}}>{formatSize(file.size)}</td>
                <td style={{verticalAlign: "middle", whiteSpace: "nowrap"}}>{formatDate(file.modified)}</td>
                <td style={{verticalAlign: "middle", textAlign: "right"}}>
                  {onSelect
                    ? <Button size="sm" onClick={() => onSelect(file.url)}>{file.url === selectedUrl ? "In use" : "Use"}</Button>
                    : <Button size="sm" variant="outline-secondary" onClick={() => copyLink(file.url)}>{copied === file.url ? "Copied" : "Copy link"}</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  )
}

const MediaLibrary = ({ show, onHide, kinds = Object.keys(KINDS), onSelect, selectedUrl, title }) => (
  <Modal show={show} onHide={onHide} size="lg">
    <Modal.Header closeButton>
      <Modal.Title>{title || "Media library"}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p>Files here are public: anyone with the link can open them.</p>
      {kinds.length === 1
        ? <MediaFolder kind={kinds[0]} onSelect={onSelect} selectedUrl={selectedUrl} />
        : (
          // No fade: the Auth0 styleguide CSS hides Bootstrap 4's .fade.show panes.
          <Tabs defaultActiveKey={kinds[0]} id="media-library-tabs" mountOnEnter transition={false}>
            {kinds.map(kind => (
              <Tab key={kind} eventKey={kind} title={KINDS[kind].title}>
                <MediaFolder kind={kind} onSelect={onSelect} selectedUrl={selectedUrl} />
              </Tab>
            ))}
          </Tabs>
        )}
    </Modal.Body>
  </Modal>
)

export default MediaLibrary
